/**
 * Pure listing helpers (story 101872 — filter panel + search). Kept framework-free so the
 * filter logic is unit-testable and reusable by both the page and the mock backend.
 */
import type { WarrantyClaim, WarrantyClaimFilters } from './types';

export const EMPTY_CLAIM_FILTERS: WarrantyClaimFilters = {
  statuses: [],
  from: null,
  to: null,
};

export function hasActiveClaimFilters(filters: WarrantyClaimFilters): boolean {
  return filters.statuses.length > 0 || filters.from != null || filters.to != null;
}

export function activeClaimFilterCount(filters: WarrantyClaimFilters): number {
  return filters.statuses.length + (filters.from != null || filters.to != null ? 1 : 0);
}

/** Case-insensitive match across device name, reference, and serial. */
function matchesSearch(claim: WarrantyClaim, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    claim.deviceName.toLowerCase().includes(q) ||
    claim.ref.toLowerCase().includes(q) ||
    claim.serial.toLowerCase().includes(q)
  );
}

/** Whether the claim's submission date falls within the (inclusive) filter range. */
function matchesDateRange(claim: WarrantyClaim, from: string | null, to: string | null): boolean {
  const submitted = new Date(claim.createdAt).getTime();
  if (from != null && submitted < new Date(from).getTime()) return false;
  if (to != null) {
    // Treat `to` as inclusive of the whole day.
    const end = new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1;
    if (submitted > end) return false;
  }
  return true;
}

/** Apply search + status + date filters; result is sorted newest-submitted first. */
export function applyClaimFilters(
  claims: WarrantyClaim[],
  search: string,
  filters: WarrantyClaimFilters,
): WarrantyClaim[] {
  return claims
    .filter((c) => matchesSearch(c, search))
    .filter((c) => filters.statuses.length === 0 || filters.statuses.includes(c.status))
    .filter((c) => matchesDateRange(c, filters.from, filters.to))
    .sort((a, b) => {
      const t = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return t !== 0 ? t : b.id.localeCompare(a.id);
    });
}
